<template>
    <div class="chat-messages" ref="scroller" @scroll="onScroll">
        <div class="chat-load-more" v-if="hasMore">
            <a href="#" @click.prevent="loadOlder">Załaduj starsze wiadomości</a>
        </div>
        <template v-for="(message, idx) in messages">
            <div class="chat-day-separator" v-if="isNewDay(idx)" :key="'day-' + message._id">
                {{ message.createdAt | moment("D MMMM YYYY") }}
            </div>
            <div class="chat-message" :class="{ 'chat-message-own': isOwn(message) }" :key="message._id">
                <div class="chat-message-author" v-if="!isOwn(message)">{{ message.authorName }}</div>
                <div class="chat-message-bubble" v-html="renderText(message)"></div>
                <div class="chat-message-time">{{ message.createdAt | moment("HH:mm") }}</div>
            </div>
        </template>
        <div v-if="!messages.length && !state.isLoadingMessages" class="chat-empty">Brak wiadomości — napisz pierwszą!</div>
    </div>
</template>

<script>
import ChatManager from '../../ChatManager.js';
import SystemUsersManager from '../../SystemUsersManager.js';

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

export default {
    name: 'ChatMessageList',
    props: {
        room: Object
    },
    data: function() {
        return {
            ChatManager,
            state: ChatManager.state,
            stickToBottom: true,
        };
    },
    computed: {
        messages: function() {
            return this.state.messagesByRoom[String(this.room._id)] || [];
        },
        hasMore: function() {
            return !!this.state.hasMoreByRoom[String(this.room._id)];
        }
    },
    watch: {
        messages: function(newVal, oldVal) {
            // autoscroll tylko gdy user jest przy dole (nie podczas przeglądania historii)
            if (this.stickToBottom) this.$nextTick(this.scrollToBottom);
        },
        'room._id': function() {
            this.stickToBottom = true;
            this.$nextTick(this.scrollToBottom);
        }
    },
    mounted: function() {
        this.scrollToBottom();
    },
    methods: {
        isOwn: function(message) {
            return String(message.authorId) == String(ChatManager.getMyUserId());
        },
        isNewDay: function(idx) {
            if (idx == 0) return true;
            const prev = new Date(this.messages[idx - 1].createdAt);
            const curr = new Date(this.messages[idx].createdAt);
            return prev.toDateString() != curr.toDateString();
        },
        renderText: function(message) {
            // escape całości, potem podmiana tokenów wzmianek na chipy i tagów zamówień na linki
            const me = String(ChatManager.getMyUserId());
            let html = escapeHtml(message.text || '');
            html = html.replace(/@\[u:([0-9a-fA-F]{24})\]/g, (m, id) => {
                const cls = String(id) == me ? 'chat-mention chat-mention-me' : 'chat-mention';
                return '<span class="' + cls + '">@' + escapeHtml(SystemUsersManager.getName(id)) + '</span>';
            });
            // nazwa w tokenie jest już zescapowana (cały tekst przeszedł przez escapeHtml)
            html = html.replace(/#\[o:([0-9a-fA-F]{24})\|([^\]\n]*)\]/g, (m, id, name) => {
                return '<a class="chat-order-link" href="#/order/' + id + '" title="Otwórz zamówienie">#' + name + '</a>';
            });
            return html.replace(/\n/g, '<br>');
        },
        scrollToBottom: function() {
            const el = this.$refs.scroller;
            if (el) el.scrollTop = el.scrollHeight;
        },
        onScroll: function() {
            const el = this.$refs.scroller;
            if (!el) return;
            this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
            if (el.scrollTop < 40 && this.hasMore && !this._loadingOlder) this.loadOlder();
        },
        loadOlder: async function() {
            if (this._loadingOlder) return;
            this._loadingOlder = true;
            const el = this.$refs.scroller;
            const prevHeight = el ? el.scrollHeight : 0;
            try {
                const added = await ChatManager.loadOlder(this.room._id);
                if (added && el) {
                    // utrzymaj pozycję scrolla po doklejeniu starszych wiadomości na górze
                    this.$nextTick(() => { el.scrollTop = el.scrollHeight - prevHeight; });
                }
            } catch (e) {
                this.$h.commonApiError(e);
            } finally {
                this._loadingOlder = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.chat-messages {
    padding: 12px 14px;
    overflow-y: auto;
}

.chat-load-more {
    text-align: center;
    padding: 6px 0 12px;
    font-size: 12px;
}

.chat-day-separator {
    text-align: center;
    font-size: 11px;
    opacity: 0.5;
    margin: 12px 0 8px;
}

.chat-message {
    max-width: 85%;
    margin-bottom: 10px;
}

.chat-message-own {
    margin-left: auto;
    text-align: right;
}

.chat-message-author {
    font-size: 11px;
    font-weight: 600;
    opacity: 0.65;
    margin-bottom: 2px;
}

.chat-message-bubble {
    display: inline-block;
    text-align: left;
    padding: 8px 12px;
    font-size: 13px;
    line-height: 1.4;
    background: var(--dm-surface-2, #f1f3f4);
    color: var(--dm-text, #333);
    border-radius: 14px 14px 14px 4px;
    word-break: break-word;
}

.chat-message-own .chat-message-bubble {
    background: #007bff;
    color: #fff;
    border-radius: 14px 14px 4px 14px;
}

.chat-message-time {
    font-size: 10px;
    opacity: 0.45;
    margin-top: 2px;
}

.chat-empty {
    text-align: center;
    opacity: 0.5;
    padding: 30px 0;
    font-size: 13px;
}
</style>

<style lang="scss">
/* niescope'owane — chipy wzmianek wstrzykiwane przez v-html */
.chat-message-bubble .chat-mention {
    background: rgba(0, 123, 255, 0.12);
    color: #cfe5ff;
    border-radius: 4px;
    padding: 0 3px;
    font-weight: 600;
}
.chat-message:not(.chat-message-own) .chat-message-bubble .chat-mention {
    color: #0b6dd8;
}
.chat-message-bubble .chat-mention-me {
    background: rgba(255, 193, 7, 0.35);
    color: inherit;
}

.chat-message-bubble .chat-order-link {
    background: rgba(0, 123, 255, 0.12);
    color: #0b6dd8;
    border-radius: 4px;
    padding: 0 3px;
    font-weight: 600;
    text-decoration: none;

    &:hover { text-decoration: underline; }
}
.chat-message-own .chat-message-bubble .chat-order-link {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
}
</style>
